import { useId } from 'react';

import type { VenueDescriptionSource } from '../../../../api/venues';

import { DescriptionLabelRow } from './DescriptionLabelRow';
import { DescriptionSavingNote } from './DescriptionSavingNote';
import { Textarea } from './Textarea';

export const DESCRIPTION_MAX_LENGTH = 5000;

const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export interface DescriptionFieldProps {
  value: string;
  onChange: (value: string) => void;
  source: VenueDescriptionSource;
  editedBy: string | null;
  rows: number;
}

/** The description editor: label row, a 5,000 character box, a note on syncing and a counter. */
export function DescriptionField({
  value,
  onChange,
  source,
  editedBy,
  rows,
}: DescriptionFieldProps) {
  const inputId = useId();
  const noteId = useId();
  const limitId = useId();
  return (
    <div>
      <DescriptionLabelRow
        inputId={inputId}
        source={source === 'nebari' ? null : source}
        who={editedBy}
      />
      <Textarea
        id={inputId}
        value={value}
        onChange={onChange}
        rows={rows}
        maxLength={DESCRIPTION_MAX_LENGTH}
        placeholder="Tell customers what the venue is like — the room, the bar, parking."
        describedBy={`${noteId} ${limitId}`}
      />
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 6 }}>
        <span
          id={noteId}
          style={{
            flex: 1,
            fontSize: 11,
            lineHeight: 1.45,
            color: source === 'fareharbor' ? 'var(--nb-ink-2)' : 'var(--nb-muted)',
          }}
        >
          <DescriptionSavingNote source={source} />
        </span>
        <span id={limitId} style={VISUALLY_HIDDEN}>
          Up to {DESCRIPTION_MAX_LENGTH.toLocaleString('en-US')} characters
        </span>
        <span
          aria-hidden="true"
          className="nb-tabular"
          style={{ fontSize: 11, fontWeight: 600, color: 'var(--nb-muted)', whiteSpace: 'nowrap' }}
        >
          {value.length.toLocaleString('en-US')} / {DESCRIPTION_MAX_LENGTH.toLocaleString('en-US')}
        </span>
      </div>
    </div>
  );
}
